import { ButtonWithIcon } from './ButtonWithIcon';
import { DPR } from '@constants/common.const';
import { ShadowNineSlice } from './Shadow9Slice';
import { GameConfigService } from '@services/core/GameConfigService';

interface FooterBarOptions {
  scene: Phaser.Scene;
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  onAction: (label: FooterAction) => void;
}

export enum FooterAction {
  RULES = 'Rules',
  HISTORY = 'History',
  DICE = 'Dice',
  MILESTONES = 'Milestones',
  RANK = 'Rank',
  GOALS = 'Goals',
}

export class FooterBar extends Phaser.GameObjects.Container {
  private bgRT!: Phaser.GameObjects.RenderTexture;
  private diceBubbleArc!: Phaser.GameObjects.Arc;
  private buttons: ButtonWithIcon[] = [];
  private diceButton: ButtonWithIcon | undefined;
  private buttonByAction = new Map<FooterAction, ButtonWithIcon>();
  private footerWidth: number;
  private footerHeight: number;
  private radius: number;
  private opts: FooterBarOptions;

  private readonly diceButtonRadius = 32 * DPR;
  private shadow: ShadowNineSlice;
  private configService = new GameConfigService();

  constructor(options: FooterBarOptions) {
    super(options.scene, options.x, options.y);
    this.scene.add.existing(this);

    this.footerWidth = options.width;
    this.footerHeight = options.height;
    this.radius = options.radius;
    this.opts = options;

    this.shadow = new ShadowNineSlice(
      this.scene,
      0,
      options.height / 2,
      options.width,
      options.height,
      'rounded',
    );

    // RenderTexture for the static white background — baked once after layout
    // setOrigin(0,0) so position drives the top-left corner, matching the old Graphics offset
    this.bgRT = options.scene.add.renderTexture(
      -options.width / 2,
      0,
      options.width,
      options.height,
    );
    this.bgRT.setOrigin(0, 0);

    // Arc for the dice bubble — stays outside the RT so it can overflow the footer bounds
    // Arc caches its earcut result; no per-frame recomputation unlike Graphics
    this.diceBubbleArc = options.scene.add.arc(
      0,
      0,
      this.diceButtonRadius,
      0,
      360,
      false,
      new GameConfigService().getColor('--color-accent-1'),
    );

    this.add([this.shadow, this.bgRT, this.diceBubbleArc]);

    this.createButtons();
    this.redraw(options.width, options.height);
    this.layoutButtons();
  }

  private createButtons() {
    const items = [
      { action: FooterAction.RULES, text: 'footer.rules', icon: 'rules' },
      {
        action: FooterAction.MILESTONES,
        text: 'footer.milestones',
        icon: 'stadium',
      },
      { action: FooterAction.DICE, text: undefined, icon: 'DiceSmall' },
      { action: FooterAction.GOALS, text: 'footer.goals', icon: 'goals' },
      { action: FooterAction.HISTORY, text: 'footer.history', icon: 'book' },
    ];

    this.buttons = items.map((item) => {
      const btn = new ButtonWithIcon({
        scene: this.scene,
        iconKey: item.icon,
        iconSize: item.action === FooterAction.DICE ? 32 * DPR : 24 * DPR,
        text: item.text,
        x: 0,
        y: 0,
        hitRadius:
          item.action === FooterAction.DICE
            ? this.diceButtonRadius //  matches blue bubble
            : undefined,
        onClick: () => this.opts.onAction(item.action),
      });

      if (item.action === FooterAction.DICE) {
        this.diceButton = btn;
      }

      this.buttonByAction.set(item.action, btn);
      this.add(btn);
      return btn;
    });
  }

  public redraw(width: number, height: number) {
    this.footerWidth = width;
    this.footerHeight = height;
    this.bgRT.setPosition(-width / 2, 0);
    this.bgRT.resize(width, height);
    this.shadow.resize(width, height);
  }

  public layoutButtons() {
    const uiWidth = this.footerWidth;
    const footerHeight = this.footerHeight;
    const paddingHorizontal = 16 * DPR;

    if (!this.buttons.length) return;

    const leftEdge = -uiWidth / 2 + paddingHorizontal;
    const rightEdge = uiWidth / 2 - paddingHorizontal;

    // Gather non-dice buttons in order
    const sideButtons = this.buttons.filter((b) => b !== this.diceButton);
    const leftButtons = sideButtons.slice(0, Math.ceil(sideButtons.length / 2));
    const rightButtons = sideButtons.slice(Math.ceil(sideButtons.length / 2));

    // One uniform pitch between adjacent button centers, dice slot included.
    // Outermost buttons keep a half-pitch margin to the padded edges; with
    // uneven halves the tighter side wins so spacing stays uniform throughout.
    const leftPitch = leftButtons.length
      ? -leftEdge / (leftButtons.length + 0.5)
      : Infinity;
    const rightPitch = rightButtons.length
      ? rightEdge / (rightButtons.length + 0.5)
      : Infinity;
    const pitch = Math.min(leftPitch, rightPitch);

    // Fan out from the center: nearest left button at -pitch, next at -2*pitch…
    leftButtons.forEach((btn, i) => {
      btn.x = -pitch * (leftButtons.length - i);
    });
    rightButtons.forEach((btn, i) => {
      btn.x = pitch * (i + 1);
    });

    // Dice always in the center
    if (this.diceButton) {
      this.diceButton.x = 0;
    }

    // Align all buttons vertically
    this.buttons.forEach((btn) => {
      btn.y = footerHeight / 2;
      const bounds = btn.getBounds();
      const footerCenterWorldY = this.y + footerHeight / 2;
      const btnCenterWorldY = (bounds.top + bounds.bottom) / 2;
      btn.y += footerCenterWorldY - btnCenterWorldY;
    });

    if (this.diceButton) {
      this.diceBubbleArc.setPosition(this.diceButton.x, this.diceButton.y);
    }

    this.bakeBackground();
  }

  public setButtonEnabled(action: FooterAction, enabled: boolean): void {
    const btn = this.buttonByAction.get(action);
    if (!btn) return;
    btn.setDisabled(!enabled);
  }

  public setDiceEnabled(
    enabled: boolean,
    options?: { onDisabledClick?: () => void | Promise<void> },
  ) {
    const diceBtn = this.diceButton;
    if (diceBtn) {
      diceBtn.setDisabled(!enabled, options);
      const bgColor =
        this.configService.getColor('--z-dice-btn-background') ??
        this.configService.getColor('--color-accent-1');
      this.diceBubbleArc.setFillStyle(bgColor, enabled ? 1 : 0.8);
    }
  }

  private bakeBackground(): void {
    this.bgRT.clear();

    const gfx = this.scene.add.graphics();
    gfx.fillStyle(this.configService.getColor('--color-base-1')!, 0.9);
    gfx.fillRoundedRect(0, 0, this.footerWidth, this.footerHeight, this.radius);
    this.bgRT.draw(gfx, 0, 0);
    gfx.destroy();
  }
}
